// =============================================================================
// domain/heatmap.ts — the Day × Hour heatmap.
// -----------------------------------------------------------------------------
// "P&L by session window" — a grid of weekday × hour-of-entry, coloured by
// money. It is the card that answers "when do I actually make money", and on
// Josh's book it is the one that shows NY-AM bleeding.
//
// ── 🚨 ITS POPULATION IS THE LOOSER SET, LIKE TOP PATTERNS AND THE GAUGES ──
//
// Beta calls `renderHeatmap(trades, 'pnl', unit)` where `trades` is
// `tradesForActive()` — range + account selection + facets and NOTHING ELSE. The
// renderer's own filter is `t.core?.date && t.core?.timeIn`: no
// `countsForMetrics`, no annotation gate.
//
// So this card counts SIM and market-replay trades where the weekday and session
// cards two rows above it do not. That is the third distinct population on one
// screen, and it is beta's (D77, D80).
//
// ── THE HOUR IS THE HOUR OF ENTRY, AND ONLY THAT ──
//
// `parseInt(timeIn.split(':')[0])`. A trade opened at 09:58 and closed at 11:04
// belongs entirely to the 9am cell; nothing is apportioned across the hours it
// was held. That is a real limitation of the card rather than a bug, and it is
// why the label says "session window" rather than "time in market".
// =============================================================================

/** What the heatmap needs from a trade. */
export interface HeatmapTrade {
  /** The day-boundary-resolved date — the caller applies the mode. */
  effectiveDate?: string | null;
  /** `HH:MM` or `HH:MM:SS`. */
  timeIn?: string | null;
  netPnl?: number | null;
}

export interface HeatCell {
  dow: number;
  hour: number;
  pnl: number;
  count: number;
  wins: number;
  losses: number;
}

export interface HeatmapGrid {
  /** Day-of-week indices, in display order. */
  dows: number[];
  /** Hour numbers, in display order. */
  hours: number[];
  /** Keyed `${dow}_${hour}`. Absent = no trades in that window. */
  cells: Map<string, HeatCell>;
  /** The largest absolute cell P&L, for the colour ramp. */
  maxAbs: number;
}

export const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** `0 → 12a`, `9 → 9a`, `12 → 12p`, `18 → 6p`. */
export function hourLabel(h: number): string {
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
}

export const cellKey = (dow: number, hour: number) => `${dow}_${hour}`;

/** `2026-07-31` → 5 (Friday). UTC so the label cannot shift by timezone. */
function dayOfWeek(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getUTCDay();
}

/**
 * Build the grid.
 *
 * ⚠ ROWS: Mon–Fri ALWAYS, with Sunday prepended and Saturday appended only when
 * they carry trades. Same asymmetry as the weekday breakdown and for the same
 * reason — a futures trader has no weekend session, so two permanently empty
 * rows are noise, but a missing Wednesday would read as a rendering fault.
 *
 * ⚠ Sunday goes FIRST and Saturday LAST, which is the calendar order rather than
 * the trading-week order. Beta does this and it matters on a futures book: the
 * Sunday evening session opens the week, so putting it at the top reads as "the
 * week starts here" — which is what it is.
 *
 * ⚠ COLUMNS: at least 6am–4pm, expanded to whatever the data reaches. A book
 * with one 03:00 trade shows midnight onward; a book that only trades the RTH
 * open still gets the full core window rather than three columns, so the grid
 * does not change shape every time the filter moves.
 *
 * ⚠ A trade with NO `timeIn` is dropped entirely — it cannot be placed in an
 * hour. Beta says so on the empty state: "Imported trades need a non-empty
 * time-in." That is a real gap in imported data, not a filter.
 */
export function buildHeatmap(trades: readonly HeatmapTrade[]): HeatmapGrid | null {
  const cells = new Map<string, HeatCell>();
  const usedDows = new Set<number>();
  let minHour = 24;
  let maxHour = 0;

  for (const t of trades) {
    const d = t.effectiveDate ?? '';
    if (!d || !t.timeIn) continue;
    const hour = Number.parseInt(String(t.timeIn).split(':')[0] ?? '', 10);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue;

    const dow = dayOfWeek(d);
    if (!Number.isInteger(dow)) continue;

    const k = cellKey(dow, hour);
    let cell = cells.get(k);
    if (!cell) {
      cell = { dow, hour, pnl: 0, count: 0, wins: 0, losses: 0 };
      cells.set(k, cell);
    }
    const net = t.netPnl ?? 0;
    cell.pnl += net;
    cell.count++;
    // ⚠ Win/loss on the SIGN, and a scratch is neither — the same rule every
    // other aggregate in this client uses, so a flat day is not a loss.
    if (net > 0) cell.wins++;
    else if (net < 0) cell.losses++;

    if (hour < minHour) minHour = hour;
    if (hour > maxHour) maxHour = hour;
    usedDows.add(dow);
  }

  if (cells.size === 0) return null;

  const dows: number[] = [];
  if (usedDows.has(0)) dows.push(0);
  dows.push(1, 2, 3, 4, 5);
  if (usedDows.has(6)) dows.push(6);

  const startHour = Math.min(minHour, 6);
  const endHour = Math.max(maxHour, 16);
  const hours: number[] = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);

  let maxAbs = 0;
  for (const c of cells.values()) if (Math.abs(c.pnl) > maxAbs) maxAbs = Math.abs(c.pnl);

  return { dows, hours, cells, maxAbs };
}

/**
 * The cell's opacity, 0–1.
 *
 * ⚠ FLOORED AT 0.18, not at 0. A cell holding a trade must be visibly distinct
 * from an empty one even when its P&L rounds to nothing — otherwise "I traded
 * here and broke even" and "I have never traded here" render identically, and
 * they are opposite findings.
 */
export function cellAlpha(pnl: number, maxAbs: number): number {
  const intensity = maxAbs > 0 ? Math.min(1, Math.abs(pnl) / maxAbs) : 0;
  return 0.18 + intensity * 0.55;
}

/**
 * The compact money label.
 *
 * ⚠ `$5.5k` above a thousand, full `+$298` below. Twenty-four columns leave
 * roughly forty pixels per cell, and `-$5,504.00` does not fit — but the
 * abbreviation is only applied where it is needed, so small cells keep their
 * exact figure.
 *
 * ⚠ THE SIGN IS PREPENDED MANUALLY IN THE ABBREVIATED BRANCH, and beta's own
 * comment records why: an earlier version handled only the negative case, so
 * positives at or above $1k rendered as "$1.3k" while everything under it showed
 * a "+". A column of numbers where only some carry a sign is unreadable.
 */
export function cellLabel(pnl: number, money: (n: number) => string): string {
  if (Math.abs(pnl) < 1000) return money(pnl);
  const sign = pnl > 0 ? '+' : pnl < 0 ? '-' : '';
  return `${sign}$${Math.round(Math.abs(pnl) / 100) / 10}k`;
}
